import { useMemo } from 'react';

import { useSchoolListingById } from '@hooks/useSchoolListing';
import { useShift } from '@hooks/useShift';
import { useSubscription } from '@hooks/useSubscription';
import { useTeacherProfile } from '@hooks/useTeacherProfile';

import { TeacherShiftFilters } from '@utils/helpers/shiftFilters';

/**
 * Custom hook for managing TeacherListing data fetching and filtering
 * @param listingId - The ID of the school listing
 * @returns Filtered listing data and loading states
 */
export const useTeacherListingData = (listingId: string) => {
  const {
    listingById,
    isPending: isListingPending,
    isError: isListingError,
  } = useSchoolListingById(listingId);

  const { teacher, isPending: isProfilePending, isError: isProfileError } = useTeacherProfile();

  const {
    subscriptions,
    isPending: isSubscriptionsPending,
    isError: isSubscriptionsError,
  } = useSubscription();

  const { shiftReactions } = useShift();

  // Filter shifts to only show open shifts where teacher hasn't reacted yet
  const filteredListing = useMemo(() => {
    if (!listingById) return null;

    const filteredShifts = TeacherShiftFilters.filterListingShifts(
      listingById.shifts,
      shiftReactions,
    );

    return {
      ...listingById,
      shifts: filteredShifts,
    };
  }, [listingById, shiftReactions]);

  const currentSubscription = subscriptions?.find((sub) => sub.listing_id === listingId);
  const schoolName = listingById?.user_school?.primary_education?.vestigingsnaam;

  const isLoading = isListingPending || isSubscriptionsPending || isProfilePending;
  const isError = isListingError || isSubscriptionsError || isProfileError;

  return {
    filteredListing,
    teacher,
    currentSubscription,
    schoolName,
    subscriptions,
    isLoading,
    isError,
  };
};
